/**
 * Row Group Utilities
 * Extracted from App.js for form field layout management
 */

/**
 * Converts flat form fields array into row groups based on field widths
 * This is the exact algorithm from App.js - DO NOT MODIFY
 */
export const migrateFormFieldsToRowGroups = (formFields) => {
  if (!formFields || formFields.length === 0) {
    return [{ id: `row_${Date.now()}`, fields: [] }];
  }
  
  const rowGroups = [];
  let currentRow = { id: `row_${Date.now()}_0`, fields: [] };
  let currentRowWidth = 0;
  
  formFields.forEach((field, index) => {
    const rawWidth = parseInt(field.width || '100');
    // Handle grid column format (1-12) vs percentage format (25-100)
    // If width is 12 or less, convert to percentage equivalent
    const fieldWidth = rawWidth <= 12 ? Math.round(rawWidth / 12 * 100) : rawWidth;
    
    // Full-width fields (100% or 12 columns) always get their own row
    if (fieldWidth >= 100) {
      // Save current row if it has fields
      if (currentRow.fields.length > 0) {
        rowGroups.push(currentRow);
      }
      // Add full-width field as its own row
      rowGroups.push({ id: `row_${Date.now()}_${index}`, fields: [field] });
      // Start new row
      currentRow = { id: `row_${Date.now()}_${index}_next`, fields: [] };
      currentRowWidth = 0;
    } else if (currentRowWidth + fieldWidth > 100) {
      // Would exceed 100%, start new row
      if (currentRow.fields.length > 0) {
        rowGroups.push(currentRow);
      }
      currentRow = { id: `row_${Date.now()}_${index}`, fields: [field] };
      currentRowWidth = fieldWidth;
    } else {
      // Add to current row
      currentRow.fields.push(field);
      currentRowWidth += fieldWidth;
    }
  });
  
  // Don't forget the last row
  if (currentRow.fields.length > 0) {
    rowGroups.push(currentRow);
  }
  
  // Ensure at least one empty row if nothing was added
  if (rowGroups.length === 0) {
    rowGroups.push({ id: `row_${Date.now()}`, fields: [] });
  }
  
  return rowGroups;
};

/**
 * Calculate column span for 12-column grid based on field width
 * Handles both percentage format (25-100) and column format (1-12)
 */
export const getColumnSpan = (widthPercent) => {
  const w = parseInt(widthPercent || '100');
  // Handle grid column format (1-12) vs percentage format (25-100)
  // If width is 12 or less, treat as column span directly
  if (w <= 12) return w;
  // Otherwise treat as percentage
  if (w >= 100) return 12;
  if (w >= 75) return 9;   // 75% = 9 columns
  if (w >= 66) return 8;   // 66% = 8 columns
  if (w >= 50) return 6;
  if (w >= 33) return 4;
  if (w >= 25) return 3;
  return Math.max(1, Math.round(w / 100 * 12));
};

/**
 * Get field wrapper style (background color support + overflow protection)
 * This is the exact style from App.js with added overflow protection
 */
export const getFieldStyle = (field) => ({
  backgroundColor: field.backgroundColor || 'transparent',
  padding: field.backgroundColor ? '12px' : '0',
  borderRadius: field.backgroundColor ? '6px' : '0',
  boxSizing: 'border-box',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  maxWidth: '100%'
});

/**
 * Check if conditional field should be shown
 * This is the exact algorithm from App.js - DO NOT MODIFY
 */
export const shouldShowConditionalField = (field, allFields, getFieldValue) => {
  if (!field.conditionalField) {
    return true; // No condition, always show
  }
  
  const parentField = allFields.find(f => f.id === field.conditionalField);
  if (!parentField) {
    return false; // Parent not found, hide
  }
  
  const parentValue = getFieldValue(parentField.id) || '';
  const operator = field.conditionalOperator || 'equals';
  const targetValue = field.conditionalValue;
  
  switch (operator) {
    case 'equals':
      return Array.isArray(parentValue) ? parentValue.includes(targetValue) : parentValue === targetValue;
    case 'not_equals':
      return Array.isArray(parentValue) ? !parentValue.includes(targetValue) : parentValue !== targetValue;
    case 'contains':
      return Array.isArray(parentValue) ? parentValue.includes(targetValue) : String(parentValue || '').includes(targetValue);
    case 'not_empty':
      return !!parentValue;
    case 'empty':
      return !parentValue;
    default:
      return targetValue ? (Array.isArray(parentValue) ? parentValue.includes(targetValue) : parentValue === targetValue) : !!parentValue;
  }
};
